import { useCallback, useContext, useMemo } from 'react';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  getBuySideGroupAnchorId,
  getShowGroupChat,
  hasNotSeenDiscoveryFeature,
  scrollToElement,
} from 'utils';
import { FOURTH_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';

export default function useGroupChatSellSideDiscovery() {
  const { currentUser, updatePreferences } = useContext(CurrentUserContext);
  const {
    seenDataRoomGroupChatSellSideFeature,
    openedGroupChatDrawerSellSide,
  } = currentUser || {};

  const { buySideGroups } = useContext(MembersContext);
  const { openChatDrawer } = useContext(DataRoomContext);

  const firstGroupWithGroupChat = useMemo(
    () =>
      buySideGroups.find(({ activeChatSessionId, dataRoomMembers }) =>
        getShowGroupChat(activeChatSessionId, dataRoomMembers),
      ),
    [buySideGroups],
  );

  const showCard = useMemo(
    () =>
      !!firstGroupWithGroupChat &&
      hasNotSeenDiscoveryFeature(
        seenDataRoomGroupChatSellSideFeature,
        FOURTH_RELEASE_DISCOVERING_FEATURE,
      ),
    [firstGroupWithGroupChat, seenDataRoomGroupChatSellSideFeature],
  );

  const onUnderstood = useCallback(() => {
    if (seenDataRoomGroupChatSellSideFeature) return;
    updatePreferences({ seenDataRoomGroupChatSellSideFeature: true });
  }, [seenDataRoomGroupChatSellSideFeature, updatePreferences]);

  const markDrawerOpened = useCallback(() => {
    if (openedGroupChatDrawerSellSide) return;
    updatePreferences({ openedGroupChatDrawerSellSide: true });
  }, [openedGroupChatDrawerSellSide, updatePreferences]);

  const onGoToGroupChat = useCallback(() => {
    onUnderstood();

    if (!firstGroupWithGroupChat) return;

    const {
      _id,
      activeChatSessionId,
      closedChatSessions = [],
    } = firstGroupWithGroupChat;

    setTimeout(() => scrollToElement(`#${getBuySideGroupAnchorId(_id)}`), 0);

    markDrawerOpened();
    openChatDrawer(activeChatSessionId, activeChatSessionId, {
      activeChatSessionId,
      closedChatSessions,
    });
  }, [onUnderstood, markDrawerOpened, firstGroupWithGroupChat, openChatDrawer]);

  return {
    showCard,
    shouldHighlightGroupChatButton:
      hasNotSeenDiscoveryFeature(
        seenDataRoomGroupChatSellSideFeature,
        FOURTH_RELEASE_DISCOVERING_FEATURE,
      ) && !openedGroupChatDrawerSellSide,
    onUnderstood,
    onGoToGroupChat,
    markDrawerOpened,
  };
}
